import { useEffect } from "react";

interface UseKeyboardShortcutsProps {
	onStart: () => void;
	onStop: () => void;
	onTogglePrefs: () => void;
	onToggleHistory: () => void;
	onGenerateComments?: () => void;
	isRunning: boolean;
	disabled?: boolean;
}

export const useKeyboardShortcuts = ({
	onStart,
	onStop,
	onTogglePrefs,
	onToggleHistory,
	onGenerateComments,
	isRunning,
	disabled = false,
}: UseKeyboardShortcutsProps) => {
	useEffect(() => {
		if (disabled) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			// Ignore if user is typing in an input or textarea
			const activeElement = document.activeElement;
			const isInputFocused =
				activeElement instanceof HTMLInputElement ||
				activeElement instanceof HTMLTextAreaElement ||
				(activeElement as HTMLElement)?.isContentEditable;

			if (isInputFocused) {
				// Special case: Escape should still work to blur input if needed?
				// But Modal handles Escape globally if it's open.
				return;
			}

			// Space: Start/Stop session
			if (e.key === " " || e.code === "Space") {
				e.preventDefault();
				if (isRunning) {
					onStop();
				} else {
					onStart();
				}
			}

			// Alt + S: Toggle Preferences
			if (e.altKey && (e.key === "s" || e.key === "S")) {
				e.preventDefault();
				onTogglePrefs();
			}

			// Alt + H: Toggle History
			if (e.altKey && (e.key === "h" || e.key === "H")) {
				e.preventDefault();
				onToggleHistory();
			}

			// Alt + G: Generate Comments
			if (e.altKey && (e.key === "g" || e.key === "G")) {
				e.preventDefault();
				onGenerateComments?.();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [
		onStart,
		onStop,
		onTogglePrefs,
		onToggleHistory,
		onGenerateComments,
		isRunning,
		disabled,
	]);
};
